export const STRUCTURAL_DECISION = Object.freeze({
  KEEP: 'keep',
  REVIEW: 'review',
  REJECT: 'reject',
});

export const FILTER_STATUS = Object.freeze({
  CANDIDATE: 'candidate',
  DOWNRANKED: 'downranked',
  FILTERED: 'filtered',
  NEEDS_REVIEW: 'needs_review',
});

const STATUS_WEIGHT = Object.freeze({
  [FILTER_STATUS.CANDIDATE]: 0,
  [FILTER_STATUS.NEEDS_REVIEW]: 1,
  [FILTER_STATUS.DOWNRANKED]: 2,
  [FILTER_STATUS.FILTERED]: 3,
});

const STRUCTURAL_RULES = Object.freeze([
  {
    reason: 'government_or_identity',
    filterCategory: 'government',
    filterReason: 'government-or-public-service',
    filterStatus: FILTER_STATUS.FILTERED,
    pattern: /\b(gov|government|gouvernement|ministry|ministerstwo|public services?|citizen|civic|municipal|county|dmv|digital agency|hmrc|one login|identit[a-z]*|identity verification|digital identity|id verification|ausweis|tawakkalna|mobywatel|mojeikp|bundesagentur|governikus|federal|federation|aadhaar|uidai|digilocker|fnmt|certificado digital|uae ?pass|nafath|absher|najiz|e-?devlet|meu inss|caixa tem|carteira de trabalho|cnh digital)\b|cl@ve|cieid|行政|政府|公務|市役所|区役所|公共服务|公共服務|身分證|身份认证/i,
    explanation: '政府、身份认证或公共基础设施需求，不是独立 app 缺口。',
  },
  {
    // #312：各国邮政词表缺口。Australia Post 走真实入队路径仍 ENQUEUED——不是缓存
    // 短路，而是词表根本覆盖不到它（`post` 只以 `post & dhl` 存在于 brand_or_retail，
    // gov/government 在它任何字段里都不出现）。这里用**具名邮政商号**兜住各国邮政。
    // 刻意**不**收泛化的 `post`/`postal`：`post` 在 description 里是「发帖」，会大面积
    // 误伤社交 / creator 类 indie（#311 要根治的泛词误杀）。宁可漏，不可误。
    // DHL / InPost 已在 brand_or_retail / operations_heavy，不重复。
    reason: 'national_postal',
    filterCategory: 'government',
    filterReason: 'national-postal-service',
    filterStatus: FILTER_STATUS.FILTERED,
    pattern: /\b(australia post|auspost|royal mail|deutsche post|la poste|poste italiane|correos|japan post|posti|postnord|canada post|usps|singpost|pos malaysia|nz post|swiss post|posten)\b|郵便|邮政|우편/i,
    explanation: '国家邮政属机构自有分发，不是独立 app 缺口。',
  },
  {
    reason: 'finance_or_regulated',
    filterCategory: 'banking',
    filterReason: 'regulated-finance-or-identity',
    filterStatus: FILTER_STATUS.FILTERED,
    pattern: /\b(bank|banking|credit union|securities|brokerage|broker|insurance|insurer|tax|finance|finanz|hana bank|revolut|kalshi|polymarket|mercado pago|tamara|wero|check24|naver\s*pay|adcb|tabby|payflex|klarna|afterpay|nubank|monzo|billease|kredivo|pay over time|pay later|buy now pay later|bnpl)\b|네이버페이|銀行|银行|証券|证券|保険|保险|税務|税务/i,
    explanation: '金融和预测市场 app 带有监管、信任和牌照约束。',
  },
  {
    reason: 'betting_or_gambling',
    filterCategory: 'gambling',
    filterReason: 'betting-or-gambling',
    filterStatus: FILTER_STATUS.FILTERED,
    pattern: /\b(bet365|betano|betsson|betclic|betway|bwin|winamax|betfair|1xbet|melbet|stake|apuestas|apostas|sportsbook|sports betting|paris sportifs|poker|gambling|lottery|lotto)\b|博彩|投注|彩票|賭博|赌博/i,
    explanation: '博彩、体育投注、彩票类受牌照监管，非独立可做机会。',
  },
  {
    reason: 'content_or_rights',
    filterCategory: 'large_platform',
    filterReason: 'large-platform-or-media',
    filterStatus: FILTER_STATUS.FILTERED,
    pattern: /\b(netflix|hbo|max|dazn|peacock|disney|prime video|paramount|crunchyroll|bbc|iplayer|nhk|manga|stream|streaming|tv|sports|f1|viaplay|osn|shahid|line manga|piccoma|short drama|drama|itvx)\b|ニュース|メディア|動画|新聞|媒體|媒体/i,
    explanation: '内容、体育和 streaming 信号通常是版权或片库业务。',
  },
  {
    reason: 'large_platform',
    filterCategory: 'large_platform',
    filterReason: 'large-platform-or-media',
    filterStatus: FILTER_STATUS.FILTERED,
    // 裸 `line` 已移出：小写 line 在 description 里是日常词（`Stand in line`），
    // 打在自由文本上会把游乐园、加油站排队这类文案误判成 LINE messenger。
    // LINE 本尊改由下面那条大小写敏感规则认。`yandex|яндекс` 是新增的精确词：
    // Яндекс 是 mega-platform（与 google/naver/tencent 同级），它此前只是碰巧靠
    // `stand in line at the checkout` 里的 line 活着，收紧 line 必须由精确词接住。
    pattern: /\b(tiktok|tik tok|douyin|doubao|bytedance|snap|linkedin|amazon|aliexpress|meta|instagram|google|whatsapp|telegram|tencent|naver|bilibili|canva|apple|airbnb|booking\.com|shein|temu|shopee|lazada|spotify|pinterest|reddit|twitch|samsung|strava|doctolib|bumble|hinge|grindr|wechat|yandex|xingin|rednote|xiaohongshu|truecaller)\b|抖音|豆包|小红书|네이버|яндекс/i,
    explanation: 'mega-platform 和成熟网络型 app 不符合当前产品策略里的开放机会槽位。',
  },
  {
    // LINE messenger 单列一条：JS 正则没有内联的 (?-i:...)，而上面整条 pattern 需要
    // /i，所以「只认全词大写 LINE」只能拆出来用一条不带 /i 的规则表达。引擎本就支持
    // 多条命中（matches 数组 + reasons 去重），无需改引擎。
    // 命中 LINE 本尊（name `LINE` / desc `LINE is transforming...`），不命中
    // `Stand in line`。`OFFLINE` 这类不会误命中——词边界要求 LINE 前后不是字母。
    // 刻意**不**收 `라인`：韩语没有词边界，而 `온라인`（online）整个包含 `라인`，
    // 收了会把任何提到「在线」的韩语 indie app 当成 LINE ——正是本片要根治的误杀。
    reason: 'large_platform',
    filterCategory: 'large_platform',
    filterReason: 'large-platform-or-media',
    filterStatus: FILTER_STATUS.FILTERED,
    pattern: /\bLINE\b/,
    explanation: 'mega-platform 和成熟网络型 app 不符合当前产品策略里的开放机会槽位。',
  },
  {
    reason: 'brand_or_retail',
    filterCategory: 'regional_service',
    filterReason: 'region-bound-service',
    filterStatus: FILTER_STATUS.FILTERED,
    pattern: /\b(lidl|action|burger king|mcdonald|suntory|mister donut|duskin|dhl|post & dhl|dm-drogerie|matsukiyo|shimamura|grand frais|shopify|ticketmaster|ryanair|mercado libre|pinduoduo|joybuy|idealista|real estate|property|avito)\b|авито/i,
    explanation: '品牌、零售、marketplace 或地产类 app 反映的是自有分发，不是未满足的 app 需求。',
  },
  {
    reason: 'operations_heavy',
    filterCategory: 'local_transit',
    filterReason: 'local-transit-or-parking',
    filterStatus: FILTER_STATUS.FILTERED,
    // 裸 `ride` 已移出：它在 description 里是日常动词（komoot 的 `your next ride,
    // hike, or run`），只有网约/共享出行的搭配才是运营信号。运营商本尊改由品牌词
    // 兜住——本规则本来就是「品牌词 + 品类词」混排（bolt/easypark/keeta/...），
    // ryde（共享滑板车）/ indrive（网约车）沿用同一条腿，不是新机制。
    pattern: /\b(bolt|easypark|keeta|rocket now|ryde|indrive|food delivery|parking|ride[- ]?(?:hailing|share|sharing)|taxi|travel booking|trip\.com|transit|metro|subway|bus|train|rail|toll|commuter|inpost)\b|交通|地下鉄|電車|駐車|停车|停車/i,
    explanation: '交通、停车、外卖、物流和预订类 app 需要本地运营或供给。',
  },
  {
    reason: 'ticketing_or_admission',
    filterCategory: 'ticketing',
    filterReason: 'ticketing-or-local-admission',
    filterStatus: FILTER_STATUS.FILTERED,
    // 裸 `ticket` 已移出：它会把商号当票务（`TICKET TO THE MOON, INC.` 是 AI 美颜
    // 相机公司）。改成只在**票务搭配**里成立——真票务生意总会说「买/订/下载 票」
    // 或「数字票/车票/停车票」，商号里的孤立 ticket 不会。
    // 注意：搭配里只收 `tickets for`、不收 `tickets to`，否则 `TICKET TO THE MOON`
    // 又会命中。
    // `amusement park|theme park|virtual queue` 是新增的精确词：游乐园/主题公园的
    // 官方 app 是典型的线下履约（入场 + 排队 + 本地库存），本就属本规则的自述范围。
    // Liseberg（哥德堡游乐园）此前只是碰巧靠 `Stand in line` 里的 line 被 large_platform
    // 拦住——那是错的理由；line 收紧后由这里按正确理由接住。
    pattern: /\b(ticketing|event pass|admission pass|amusement park|theme park|virtual queue)\b|\b(?:buy|sell|book|purchase|order|download|send|discover|scan|redeem|refund)\s+(?:\w+\s+){0,2}tickets?\b|\b(?:digital|mobile|movie|cinema|concert|event|train|bus|flight|parking|season|entry|day|e-?)[- ]?tickets?\b|\btickets?\s+for\b|チケット|入場券|票务|票務|售票/i,
    explanation: '票务和入场类 app 常绑定本地库存、授权或线下履约。',
  },
  {
    reason: 'game_or_ip_business',
    filterCategory: 'games',
    filterReason: 'game-or-ip-business',
    filterStatus: FILTER_STATUS.FILTERED,
    // 单数 `game` 已移出：它在 description 里最常见的用法恰恰是「本 app 是一个
    // 游戏」（`an immersive narrative investigation game`），而独立开发者做的单个
    // 游戏正是本雷达要找的东西——本规则针对的是「IP 或发行能力主导」的生意，不是
    // 「凡是游戏」。复数 `games` 保留：谈论**多个**游戏（`Anime, games, merch` /
    // `Play games you actually enjoy`）的通常是发行商、商城或 offerwall，那才是
    // 发行能力主导的信号。
    pattern: /\b(games|gaming|rpg|puzzle|casino|dragon ball|uma musume|honor of kings)\b|ゲーム|遊戲|游戏|手游|パズル|ドラゴンボール|賽馬娘|ウマ娘|七龍珠|三國志|信長/i,
    explanation: '游戏、IP 或发行能力主导，不适合作为当前独立产品机会。',
  },
  {
    reason: 'seasonal_or_event_bound',
    filterCategory: 'seasonal_event',
    filterReason: 'seasonal-or-event-bound',
    filterStatus: FILTER_STATUS.FILTERED,
    // "seasonal" 作为主题词（seasonal planner / almanac 之类 indie 应用）会误伤，
    // 移出结构性拒绝；只保留明确活动/会展绑定的词。
    pattern: /\b(festival|expo|conference|event|holiday|christmas|summer games|winter games)\b|祭り|フェス|イベント|展会|展會|节日|節日/i,
    explanation: '活动或会展绑定，需求不稳定且难以通用化。',
  },
]);

const SURVIVING_DECISIONS = new Set([
  STRUCTURAL_DECISION.KEEP,
  STRUCTURAL_DECISION.REVIEW,
]);

export function classifyStructuralCandidate(candidate = {}) {
  const existing = existingStructuralClassification(candidate);
  if (existing) return existing;

  const context = candidateContext(candidate);
  const matches = STRUCTURAL_RULES.filter((rule) => rule.pattern.test(context.haystack));
  if (matches.length > 0) return rejectClassification(matches, context);

  // NOTE: 不再仅凭 genre 归属做结构性拒绝。genre 只是分类维度、不是机构性证据，
  // 单靠「Travel/Finance/…」整类拒绝会把 indie 产品（旅行手账、离线导览、
  // 个人记账等）误判成机构而筛掉——正是本关口要根治的问题。机构性信号一律
  // 由上面基于 name/seller/description 关键词的 STRUCTURAL_RULES 负责识别。

  if (looksLikePortableSocialSignal(context)) {
    return withDescriptionDegradation({
      decision: STRUCTURAL_DECISION.REVIEW,
      layer: 'structural',
      signal: 'portable_social',
      portableSocialSignal: true,
      filterStatus: FILTER_STATUS.CANDIDATE,
      filterReasons: [],
      filterCategory: null,
      note: '匹配便携社交/creator 形态，并且存在西方主要市场榜单缺口。这里只是给人工复核的中性标记，不是 build/research 判断。',
    }, context);
  }

  return withDescriptionDegradation({
    decision: STRUCTURAL_DECISION.KEEP,
    layer: 'structural',
    reason: 'survives_structural_filter',
    portableSocialSignal: false,
    filterStatus: FILTER_STATUS.CANDIDATE,
    filterReasons: [],
    filterCategory: null,
    note: '未命中结构性拒绝规则，交给有记忆判断层人工阅读。',
  }, context);
}

export function structuralDecisionSurvives(classification) {
  return SURVIVING_DECISIONS.has(String(classification?.decision ?? '').toLowerCase());
}

export function chartFilterFromStructuralClassification(classification) {
  if (classification?.decision !== STRUCTURAL_DECISION.REJECT) {
    return {
      filterStatus: FILTER_STATUS.CANDIDATE,
      filterReasons: [],
      filterCategory: null,
    };
  }

  return {
    filterStatus: validStatus(classification.filterStatus) ?? FILTER_STATUS.FILTERED,
    filterReasons: uniqueStrings(classification.filterReasons),
    filterCategory: nonEmptyString(classification.filterCategory),
  };
}

// 存活路径（keep / portable_social review）上的显式降级。判定用文案没有一份独立的
// descriptionEn 背书（缺失，或与展示文案同源）时，这条 keep 可能只是本地化文案让规则链
// 静默失灵的产物——打标 + 附告警文案，绝不让它无声漏过。reject 不打标：规则已命中，
// 不存在静默 keep。
function withDescriptionDegradation(classification, context) {
  if (!context?.descriptionDegraded) return classification;
  return {
    ...classification,
    descriptionDegraded: true,
    warning: '判定用文案缺少稳定语言的 descriptionEn（缺失或与展示文案同源）；机械闸英文规则链在本地化文案上可能整段失灵，此判词按降级处理、需人工复核，不能当作可信 keep。',
  };
}

function rejectClassification(matches, context) {
  const primary = matches[0];
  return {
    decision: STRUCTURAL_DECISION.REJECT,
    layer: 'structural',
    reason: primary.reason,
    reasons: uniqueStrings(matches.map((match) => match.reason)),
    category: primary.filterCategory,
    filterStatus: strongestStatus(matches.map((match) => match.filterStatus)),
    filterReasons: uniqueStrings(matches.map((match) => match.filterReason)),
    filterCategory: primary.filterCategory,
    explanation: primary.explanation,
    metadataUnknown: context.metadataUnknown || undefined,
  };
}

function existingStructuralClassification(candidate) {
  const existing = candidate?.structuralClassification
    ?? (candidate?.classification?.layer === 'structural' ? candidate.classification : null);
  if (!existing || typeof existing !== 'object') return null;
  const decision = String(existing.decision ?? '').trim().toLowerCase();
  if (![STRUCTURAL_DECISION.KEEP, STRUCTURAL_DECISION.REVIEW, STRUCTURAL_DECISION.REJECT].includes(decision)) {
    return null;
  }

  // 只有 reject 是 sticky：pool 的拒绝集合一旦拒了就永远拒，避免同一项在不同
  // run 之间反复横跳。keep/review 不复用——它们常常是 ingest 期只看过 name 时
  // 下的判决（那时 seller/description 恒为 undefined），复用会让下游拿着富集后
  // 的 seller/description 调用时短路掉，等于把信息更充分的关口降级成信息更少的。
  if (decision !== STRUCTURAL_DECISION.REJECT) {
    return null;
  }

  return {
    ...existing,
    decision,
    layer: 'structural',
    filterStatus: validStatus(existing.filterStatus) ?? FILTER_STATUS.FILTERED,
    filterReasons: uniqueStrings(existing.filterReasons),
    filterCategory: nonEmptyString(existing.filterCategory),
  };
}

function candidateContext(candidate) {
  const name = firstString(candidate?.name, candidate?.trackName, candidate?.appName);
  const seller = firstString(
    candidate?.seller,
    candidate?.sellerName,
    candidate?.artistName,
    candidate?.artist,
    candidate?.developerName,
  );
  const genre = firstString(
    candidate?.genre,
    candidate?.primaryGenre,
    candidate?.genreName,
    candidate?.primaryGenreName,
    candidate?.app?.genre,
    candidate?.app?.primaryGenre,
    candidate?.app?.genreName,
    candidate?.app?.primaryGenreName,
  );
  const genreId = firstString(candidate?.genreId, candidate?.primaryGenreId, candidate?.app?.genreId);
  // 展示用文案：按取数储面可能被本地化，只用于展示，绝不进机械闸判定。
  const displayDescription = firstString(candidate?.description, candidate?.app?.description);
  // 判定用文案：稳定语言（统一储面）的 descriptionEn。英文规则链只在稳定语言上成立，
  // 本地化文案会让整条规则链静默失灵、候选无声漏成 keep（本 issue 的核心失效模式）。
  const descriptionEn = firstString(candidate?.descriptionEn, candidate?.app?.descriptionEn);
  // 缺 descriptionEn 时回退到展示文案以保持向后兼容，但显式标记降级——绝不静默 keep。
  const description = descriptionEn ?? displayDescription;
  const descriptionDegraded = Boolean(description)
    && (!descriptionEn || descriptionEn === displayDescription);
  const haystack = [
    name,
    seller,
    description,
  ].filter(Boolean).join(' ');

  return {
    raw: candidate,
    name,
    seller,
    genre,
    genreId,
    description,
    displayDescription,
    descriptionEn,
    descriptionDegraded,
    haystack,
    metadataUnknown: isUnknownMetadataValue(seller) || isUnknownMetadataValue(genre),
  };
}

function looksLikePortableSocialSignal(context) {
  const genre = String(context.genre ?? '');
  if (!['Social Networking', 'Photo & Video'].includes(genre)) return false;
  const description = String(context.description ?? '');
  if (!/\b(friend|friends|social|share|moments|photo|photos|irl|location)\b/i.test(description)) return false;
  const strong = Array.isArray(context.raw?.strong) ? context.raw.strong : [];
  if (strong.length < 2) return false;
  const bestRank = Math.min(...strong.map((signal) => Number(signal.bestRank ?? 999)));
  const days = Math.max(...strong.map((signal) => Number(signal.days ?? 0)));
  const absent = Array.isArray(context.raw?.absentMajor) ? context.raw.absentMajor : [];
  const hasWesternGap = ['us', 'gb', 'ca', 'au'].some((market) => absent.includes(market));
  return bestRank <= 12 && days >= 4 && hasWesternGap;
}

function strongestStatus(statuses) {
  return statuses
    .map((status) => validStatus(status))
    .filter(Boolean)
    .sort((a, b) => STATUS_WEIGHT[b] - STATUS_WEIGHT[a])[0] ?? FILTER_STATUS.FILTERED;
}

function validStatus(value) {
  const status = String(value ?? '').trim();
  return Object.values(FILTER_STATUS).includes(status) ? status : null;
}

function firstString(...values) {
  for (const value of values) {
    const normalized = nonEmptyString(value);
    if (normalized) return normalized;
  }
  return null;
}

function nonEmptyString(value) {
  const normalized = String(value ?? '').trim();
  return normalized || null;
}

function normalizeToken(value) {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function uniqueStrings(values) {
  return [...new Set((Array.isArray(values) ? values : [])
    .map((value) => nonEmptyString(value))
    .filter(Boolean))];
}

function isUnknownMetadataValue(value) {
  const normalized = normalizeToken(value);
  return !normalized || normalized === 'unknown' || normalized === 'unknown seller' || normalized === 'unknown genre';
}
